<script setup lang="ts">
import { userValidateCardCreate } from "@innoclouds/api";
import { useTemplateRefsList } from "@vueuse/core";
import type { Html5QrcodeResult } from "html5-qrcode";
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
import IconAnimatedQr from "#/assets/iconsDynamic/AnimatedQr.vue";
import CardReaderOk from "#/assets/iconsDynamic/CardReaderOk.vue";
import CardReaderRetry from "#/assets/iconsDynamic/CardReaderRetry.vue";
import LoyaltyMark from "#/assets/iconsDynamic/LoyaltyMark.vue";
import { CameraScanner } from "#/components/QrCameraScanner";
import { vAutoFocus } from "#/directives/vAutoFocus";
import { useCartStore } from "#/store/cart";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import { useUserStore } from "#/store/user";
import { emitter } from "#/utils/emitter";

const { t } = useTranslation();
const userStore = useUserStore();
const storeMain = useMainStore();
const themeStore = useThemeStore();
const { is_OpenLoyal } = storeToRefs(storeMain);
const cartStore = useCartStore();
const cardInputManualRef = ref<HTMLInputElement>();
const errorDigits = ref<string | null>(null);
const rawDigits = ref("");
const showCameraScanner = ref(false);
let isSending: boolean = false;
let prevScanValue: string | null = null;

const codeValues = ref<string[]>([]);
const codeInputRefs = useTemplateRefsList<HTMLInputElement>();

type StateKey = "wait" | "success" | "not_found";

const watchDigits = computed({
	get: () => rawDigits.value,
	set: (value: string) => {
		rawDigits.value = value.replace(/\s/g, "");
	},
});

const current = computed(() => cartStore.currentState as StateKey);
const isOpen = computed(() => cartStore.isDrawerOpen);

const statusText = computed(() => {
	if (isPromoCode.value) return t(`card-reader.status.wait.text`);
	if (current.value === "wait") return t(`card-reader.status.wait.text`);
	if (current.value === "success") return t(`card-reader.status.success.text`);
	if (current.value === "not_found") return t(`card-reader.status.not-found.text`);
	return "";
});
const titleText = computed(() => {
	if (current.value === "wait") return "";
	if (current.value === "success") return t(`card-reader.status.success.title`);
	if (current.value === "not_found") return t(`card-reader.status.not-found.title`);
	return "";
});
const subtitleText = computed(() => {
	if (current.value === "success") return t(`card-reader.subtitle.success`);
	if (themeStore.data?.loyalty_auth_text?.length) return themeStore.data?.loyalty_auth_text;
	if (themeStore.data?.kiosk_show_loyalty_phone_input_button) return t(`card-reader.subtitle.phone`);
	return t(`card-reader.subtitle.card`);
});
const iconMode = computed(() => {
	if (themeStore.data?.loyalty_auth_animation === "qr_code") return "qr";
	return "nfc";
});

const statusClass = computed(() => ({
	ok: current.value === "success",
	warn: current.value === "not_found",
}));

const nfcClass = computed(() => {
	const elements: string[] = [];
	if (current.value === "wait") elements.push("pulse", "show-x");
	if (current.value === "not_found") elements.push("pulse", "show-x");
	if (current.value === "success") elements.push("pulse", "show-ok");
	return elements;
});

const showXMark = computed(() => current.value === "not_found");
const showCheckMark = computed(() => current.value === "success");
const showRetry = computed(() => current.value === "not_found");
const showRetryButton = computed(() => ["not_found"].includes(current.value));

const isPromoCode = computed(() => themeStore.data?.kiosk_auth_overlay?.kiosk_auth_promo_code);
const propmoLength = computed(() => themeStore.data?.kiosk_auth_overlay?.kiosk_auth_promo_code_len || 4);
const promoLayout = computed(() => {
	if (
		themeStore.data?.kiosk_auth_overlay?.kiosk_auth_promo_code_keyboard &&
		["en", "ru"].includes(themeStore.data?.kiosk_auth_overlay?.kiosk_auth_promo_code_keyboard as string)
	) {
		emitter.emit("keyboard:language", themeStore.data?.kiosk_auth_overlay?.kiosk_auth_promo_code_keyboard);
		return "default";
	}

	return "numbers";
});

const liveAnnouncement = computed(() => {
	if (current.value === "wait") return t(`card-reader.anouncment.wait`);
	if (current.value === "success") return t(`card-reader.anouncment.success`);
	if (current.value === "not_found") return t(`card-reader.anouncment.not-found`);
	return "";
});

const openLoyalModal = () => {
	cartStore.closeDrawer();
	if (storeMain.isLoyalSuccessScreen) {
		storeMain.isLoyalSuccessScreen = false;
	}
	is_OpenLoyal.value = true;
};

const authorizeCard = async (cardNumber: string) => {
	if (isSending) return;
	isSending = true;
	cartStore.error = "";
	errorDigits.value = null;
	try {
		await userValidateCardCreate({ card_number: cardNumber.trim() }).then(userStore.auth);

		storeMain.loadCart();
		cartStore.setState("success");
		cartStore.isDrawerOpen = false;
		cartStore.showNotification(t(`card-reader.notification.success`));
		showCameraScanner.value = false;
		emitter.emit("keyboard:close");
		isSending = false;
		prevScanValue = null;
	} catch (error: any) {
		cartStore.error = error.response?.data?.user_friendly_message || t(`card-reader.error`);
		cartStore.setState("not_found");
		errorDigits.value = watchDigits.value;
		watchDigits.value = "";
		isSending = false;
		throw error;
	}
};

const normalizeCardNumber = (raw: string): string => {
	return raw.replace(/^[\s\S]*?((?:tel|sms|smsto|mailto|callto|facetime|viber|whatsapp):)?[\s\S]*?(%2B)?[\s\S]*?/i, (_, __, pct) => (pct ? "+" : ""));
};

const onScanSuccess = (decodedText: string, decodedResult: Html5QrcodeResult) => {
	if (isSending) return;

	const decodedGeneralText = decodedText ? decodedText : decodedResult?.result?.text;
	if (!decodedGeneralText) return;
	if (prevScanValue === decodedGeneralText) return;
	const cardNumber = normalizeCardNumber(decodedGeneralText);
	authorizeCard(cardNumber).catch(() => {
		prevScanValue = cardNumber;
		cartStore.showNotification(cartStore.error);
	});
};

const openCameraScanner = () => {
	showCameraScanner.value = true;
};

const closeCameraScanner = () => {
	cartStore.setState("wait");
	showCameraScanner.value = false;
};

const is_hide_stick = computed(() => {
	const value = themeStore.data?.kiosk_hide_manual_input_loyalty_card;
	if (value === null || value === undefined) return false;
	return value;
});

const retryScan = () => {
	cartStore.setState("wait");
	cartStore.error = "";
	watchDigits.value = "";
	codeValues.value = [];
	errorDigits.value = null;
};

const closeDrawer = () => {
	cartStore.closeDrawer();
	errorDigits.value = null;
	watchDigits.value = "";
};

const processCard = () => {
	cartStore.setState("wait");
	if (watchDigits.value?.trim()?.length) authorizeCard(watchDigits.value.trim()).catch(() => {});
};

const handleSheetClick = (e: MouseEvent) => {
	e.stopPropagation();
	if (document.activeElement !== cardInputManualRef.value) {
		focusManualInput();
	}
};

const focusManualInput = () => {
	if (showCameraScanner.value) return;
	nextTick(() => {
		if (cardInputManualRef.value) {
			cardInputManualRef.value.focus();
			if (rawDigits.value) {
				cardInputManualRef.value.select();
			}
		}
	});
};

const handler = (e: KeyboardEvent) => {
	const isInputFocused = document.activeElement === cardInputManualRef.value;
	if (!isOpen.value || isInputFocused || isPromoCode.value) return;

	const enteredSymbol = e.key.toLowerCase();

	if (enteredSymbol === "backspace") {
		watchDigits.value = watchDigits.value.slice(0, -1);
		return;
	}
	if (enteredSymbol === "escape") return closeDrawer();
	if (enteredSymbol === "r" && showRetry.value) return retryScan();
	if (enteredSymbol === "c" && current.value === "success") return closeDrawer();
	if (enteredSymbol === "enter") return processCard();

	watchDigits.value += enteredSymbol;
};

const focusCodeInput = (index: number) => {
	nextTick(() => {
		const input = codeInputRefs.value[index];
		if (input) input.focus();
	});
};

const onCodeInput = (event: Event, index: number) => {
	const target = event.target as HTMLInputElement;
	const value = target.value.slice(-1);

	codeValues.value[index] = value;
	target.value = value;

	if (value && index < propmoLength.value - 1) focusCodeInput(index + 1);
};

const onCodeKeyDown = (event: KeyboardEvent, index: number) => {
	if (event.key !== "Backspace") return;
	if (!codeValues.value[index] && index > 0) focusCodeInput(index - 1);
	codeValues.value[index] = "";
};

const onCodePaste = (event: ClipboardEvent) => {
	event.preventDefault();
	const pasted = event.clipboardData?.getData("text").slice(0, propmoLength.value) || "";
	codeValues.value = Array.from({ length: propmoLength.value }, (_, i) => pasted[i] || "");
	focusCodeInput(Math.min(pasted.length, propmoLength.value - 1));
};

onMounted(() => {
	document?.addEventListener("keydown", handler);
	console.log(
		"%c" + "Тут скрипт сканирования не работает, потому что при фокусе на консольку у нас теряется фокус с инпута, с реальным HID работает корректно, отвечаю",
		"font-size: 24px",
	);
});

onBeforeUnmount(() => {
	closeCameraScanner();
	document?.removeEventListener("keydown", handler);
	emitter.emit("keyboard:close");
});

watch(
	codeValues,
	(list) => {
		watchDigits.value = list.join("");
	},
	{ deep: true },
);

watch(
	isOpen,
	(value) => {
		if (value && isPromoCode.value) nextTick(() => focusCodeInput(0));
	},
	{ immediate: true },
);

watchEffect(() => {
	if (isOpen.value) cartStore.error = "";
});
</script>

<template>
	<div v-if="isOpen" class="backdrop show " @click="closeDrawer" aria-hidden="true" />
	<section v-if="isOpen" class="sheet show" aria-modal="true" aria-labelledby="sheetTitle" :class="{ 'sheet_retry' : showRetry }" @click="handleSheetClick">
		<CameraScanner v-if="showCameraScanner" @scan-success="onScanSuccess" @close-scanner="closeCameraScanner" />
		<header class="sheet_header">
			<div class="sheet_title" id="sheetTitle">{{ statusText }}</div>
			<div class="sheet_status" v-if="showRetry" :class="statusClass">{{ errorDigits }}</div>
			<button type="button" class="sheet_close" :aria-label="t(`card-reader.button.close`)" @click="closeDrawer">{{ t(`card-reader.button.close`) }}</button>
		</header>
		<div class="sheet_body">
			<div v-if="isPromoCode" class="sheet_card sheet_promo_code">
				<h2 class="nfc_title">{{ themeStore.data?.kiosk_auth_overlay?.kiosk_auth_promo_code_text }}</h2>

				<div class="wrapper_input_accept">
					<div class="modal_loyal_code_input_container">
						<div class="modal_loyal_code_boxes">
							<input
								v-for="(_, index) in propmoLength"
								:key="index"
								:ref="codeInputRefs.set"
								class="modal_loyal_code_box"
								type="text"
								inputmode="none"
								:data-vk-layout="promoLayout"
								maxlength="1"
								autocomplete="one-time-code"
								:autofocus="index === 0"
								:value="codeValues[index]"
								@input="(e) => onCodeInput(e, index)"
								@keydown="(e) => onCodeKeyDown(e, index)"
								@paste="onCodePaste"
								@focus="(e) => (e.target as HTMLInputElement).select()"
							>
						</div>
					</div>
				</div>
				<div class="wrapper_input_accept">
					<button v-if="showRetry" type="button" class="btn btn_ghost" @click="retryScan">{{ t(`card-reader.button.retry`) }}</button>
					<div v-else>
						<button type="button" class="btn btn_ghost" :disabled="watchDigits.length !== propmoLength" @click="showRetry ? closeDrawer : processCard()">
							{{ t(`card-reader.button.send`) }}
						</button>
					</div>
				</div>
				<div v-if="current === 'wait' && themeStore.data?.kiosk_show_loyalty_phone_input_button" class="card_phone_auth_wrapper">
					<button type="button" class="card_phone_auth_button" @click="openLoyalModal">{{ t(`card-reader.button.phone`) }}</button>
				</div>
				<div v-if="current === 'wait' && themeStore.data?.kiosk_camera_scan_loyalty_card" class="card_phone_auth_wrapper">
					<button type="button" class="card_phone_auth_button" @click="openCameraScanner">{{ t(`card-reader.button.camera`) }}</button>
				</div>
			</div>

			<div v-else class="sheet_card">
				<IconAnimatedQr v-if="iconMode === 'qr'" class="sheet_qr_frame" />
				<div v-else class="nfc" :class="nfcClass">
					<CardReaderRetry :styleRetry="showRetryButton" />
					<LoyaltyMark v-if="showXMark" />
					<div class="overlay check" :style="{ display: showCheckMark ? 'grid' : 'none' }" aria-hidden="true">
						<CardReaderOk />
					</div>
				</div>
				<h2 class="nfc_title">{{ titleText }}</h2>
				<p class="lead">{{ subtitleText }}</p>
				<div class="wrapper_input_accept">
					<input
						:style="is_hide_stick ? { position: 'absolute', left: '-9999px' } : {}"
						ref="cardInputManualRef"
						v-model="watchDigits"
						v-auto-focus
						class="nfc_title_read_input"
						type="text"
						inputmode="none"
						:placeholder="t(`card-reader.input.placeholder`)"
						aria-live="polite"
						:data-vk="!is_hide_stick"
						@keydown.enter="processCard"
					>
					<button v-if="showRetry" type="button" class="btn btn_ghost" @click="retryScan">{{ t(`card-reader.button.retry`) }}</button>
					<div v-else>
						<button v-if="!is_hide_stick" type="button" class="btn btn_ghost" @click="showRetry ? closeDrawer : processCard()">
							{{ t(`card-reader.button.send`) }}
						</button>
					</div>
				</div>
				<div v-if="current === 'wait' && themeStore.data?.kiosk_show_loyalty_phone_input_button" class="card_phone_auth_wrapper">
					<button type="button" class="card_phone_auth_button" @click="openLoyalModal">{{ t(`card-reader.button.phone`) }}</button>
				</div>
				<div v-if="current === 'wait' && themeStore.data?.kiosk_camera_scan_loyalty_card" class="card_phone_auth_wrapper">
					<button type="button" class="card_phone_auth_button" @click="openCameraScanner">{{ t(`card-reader.button.camera`) }}</button>
				</div>
			</div>
		</div>
		<div class="sr_only" aria-live="polite">{{ liveAnnouncement }}</div>
	</section>
</template>

<style lang="scss">
@use './cardReader.scss';
</style>
